interface SemiGaugeProps {
  size: "sm" | "lg";
  value: number;
  target: number;
  color: string;
  className?: string;
  trackColor?: string;
  needleColor?: string;
}

interface SemiGaugeDimensions {
  width: number;
  height: number;
  centerX: number;
  centerY: number;
  radius: number;
  strokeWidth: number;
  needleBaseWidth: number;
  needleBaseRadius: number;
  needleTipWidth: number;
}

const SEMI_GAUGE_DIMENSIONS: Record<SemiGaugeProps["size"], SemiGaugeDimensions> = {
  sm: {
    width: 156,
    height: 96,
    centerX: 78,
    centerY: 78,
    radius: 54,
    strokeWidth: 20,
    needleBaseWidth: 8,
    needleBaseRadius: 4.5,
    needleTipWidth: 2,
  },
  lg: {
    width: 232,
    height: 150,
    centerX: 116,
    centerY: 118,
    radius: 84,
    strokeWidth: 28,
    needleBaseWidth: 9,
    needleBaseRadius: 5,
    needleTipWidth: 2,
  },
};

const getNormalizedProgress = (value: number, target: number) => {
  if (target <= 0) {
    return 0;
  }

  return Math.min(Math.max(value / target, 0), 1);
};

const SemiGauge = ({
  size,
  value,
  target,
  color,
  className,
  trackColor = "#cfd5df",
  needleColor = "#34384a",
}: SemiGaugeProps) => {
  const dimensions = SEMI_GAUGE_DIMENSIONS[size];
  const progress = getNormalizedProgress(value, target);
  const shouldRenderProgressArc = progress > 0;
  const startX = dimensions.centerX - dimensions.radius;
  const endX = dimensions.centerX + dimensions.radius;
  const path = `M ${startX} ${dimensions.centerY} A ${dimensions.radius} ${dimensions.radius} 0 0 1 ${endX} ${dimensions.centerY}`;
  const progressPercent = progress * 100;
  const angleInRadians = Math.PI - progress * Math.PI;
  const needleTipRadius = dimensions.radius + dimensions.strokeWidth / 2 - 1;
  const tipX = dimensions.centerX + needleTipRadius * Math.cos(angleInRadians);
  const tipY = dimensions.centerY - needleTipRadius * Math.sin(angleInRadians);
  const directionLength =
    Math.hypot(tipX - dimensions.centerX, tipY - dimensions.centerY) || 1;
  const directionX = (tipX - dimensions.centerX) / directionLength;
  const directionY = (tipY - dimensions.centerY) / directionLength;
  const perpendicularX = -directionY;
  const perpendicularY = directionX;
  const needlePoints = [
    `${dimensions.centerX + (dimensions.needleBaseWidth / 2) * perpendicularX},${dimensions.centerY + (dimensions.needleBaseWidth / 2) * perpendicularY}`,
    `${tipX + (dimensions.needleTipWidth / 2) * perpendicularX},${tipY + (dimensions.needleTipWidth / 2) * perpendicularY}`,
    `${tipX - (dimensions.needleTipWidth / 2) * perpendicularX},${tipY - (dimensions.needleTipWidth / 2) * perpendicularY}`,
    `${dimensions.centerX - (dimensions.needleBaseWidth / 2) * perpendicularX},${dimensions.centerY - (dimensions.needleBaseWidth / 2) * perpendicularY}`,
  ].join(" ");

  return (
    <svg
      viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
      width={dimensions.width}
      height={dimensions.height}
      className={className}
      aria-hidden="true"
    >
      <path
        d={path}
        pathLength={100}
        fill="none"
        stroke={trackColor}
        strokeWidth={dimensions.strokeWidth}
        strokeLinecap="butt"
      />
      {shouldRenderProgressArc ? (
        <path
          d={path}
          pathLength={100}
          fill="none"
          stroke={color}
          strokeWidth={dimensions.strokeWidth}
          strokeLinecap="butt"
          strokeDasharray={`${progressPercent} 100`}
        />
      ) : null}
      <polygon points={needlePoints} fill={needleColor} />
      <circle
        cx={dimensions.centerX}
        cy={dimensions.centerY}
        r={dimensions.needleBaseRadius}
        fill={needleColor}
      />
    </svg>
  );
};

export default SemiGauge;
